import { useEffect, useMemo, useState } from "react";
import { AppShell, PageHeader } from "../components/AppShell";
import { IngredientPicker } from "../components/IngredientPicker";
import { UnitPills } from "../components/UnitPills";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { catColor, euro, euroPrecise } from "../lib/constants";
import { convertToBase } from "../lib/calc";
import {
    Plus,
    Trash2,
    Phone,
    Edit3,
    ChevronRight,
    ChevronDown,
    Award,
    Save,
    Truck,
    TrendingDown,
    CheckCheck,
    Sparkles,
    X,
    Check,
    List,
    FlaskConical,
    Network,
    MapPin,
    Star,
    Search,
    Filter,
} from "lucide-react";
import { toast } from "sonner";
import SupplierDiscoverDialog from "../components/SupplierDiscoverDialog";

export default function Suppliers() {
    const [suppliers, setSuppliers] = useState([]);
    const [prices, setPrices] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supDialogOpen, setSupDialogOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [priceDialogOpen, setPriceDialogOpen] = useState(false);
    const [priceForSupplier, setPriceForSupplier] = useState(null);
    const [discoverOpen, setDiscoverOpen] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [filters, setFilters] = useState({ q: "", region: "", city: "", only_preferred: false });

    const toggleExpanded = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const load = async () => {
        setLoading(true);
        try {
            const [s, p, ing] = await Promise.all([
                api.get("/suppliers"),
                api.get("/supplier-prices"),
                api.get("/ingredients"),
            ]);
            setSuppliers(s.data);
            setPrices(p.data);
            setIngredients(ing.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filteredSuppliers = useMemo(() => {
        const q = filters.q.trim().toLowerCase();
        const region = filters.region.trim().toLowerCase();
        const city = filters.city.trim().toLowerCase();
        return suppliers.filter((s) => {
            if (filters.only_preferred && !s.is_preferred) return false;
            if (region && (s.region || "").toLowerCase() !== region) return false;
            if (city && (s.city || "").toLowerCase() !== city) return false;
            if (q) {
                const hay = `${s.name} ${s.city || ""} ${s.region || ""} ${s.category || ""} ${s.notes || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [suppliers, filters]);

    const regionOptions = useMemo(() => {
        const set = new Set();
        suppliers.forEach((s) => s.region && set.add(s.region));
        return Array.from(set).sort();
    }, [suppliers]);
    const cityOptions = useMemo(() => {
        const set = new Set();
        suppliers.forEach((s) => {
            if (!s.city) return;
            if (filters.region && (s.region || "") !== filters.region) return;
            set.add(s.city);
        });
        return Array.from(set).sort();
    }, [suppliers, filters.region]);

    // best price per ingrediente (in €/kg equivalente grossolano basato su unità)
    const bestPriceByIngredient = useMemo(() => {
        const map = {};
        for (const p of prices) {
            const key = p.ingredient_id;
            if (!map[key] || p.price < map[key].price) map[key] = p;
        }
        return map;
    }, [prices]);

    const pricesBySupplier = useMemo(() => {
        const m = {};
        for (const p of prices) {
            if (!m[p.supplier_id]) m[p.supplier_id] = [];
            m[p.supplier_id].push(p);
        }
        return m;
    }, [prices]);

    const openNewSupplier = () => {
        setEditingSupplier({ name: "", contact: "", notes: "" });
        setSupDialogOpen(true);
    };
    const openEditSupplier = (s) => {
        setEditingSupplier({ ...s });
        setSupDialogOpen(true);
    };

    const saveSupplier = async (form) => {
        if (form.id) {
            await api.put(`/suppliers/${form.id}`, form);
        } else {
            await api.post("/suppliers", form);
        }
        setSupDialogOpen(false);
        await load();
    };

    const deleteSupplier = async (id) => {
        if (!window.confirm("Rimuovere questo fornitore dal tuo portale? Il fornitore rimane comunque nella rete globale (altri clienti possono usarlo). Verranno cancellati solo i tuoi listini prezzi privati.")) return;
        await api.delete(`/suppliers/${id}`);
        await load();
    };

    const togglePrefer = async (s) => {
        try {
            if (s.is_preferred) {
                await api.delete(`/suppliers/${s.id}/prefer`);
                toast.success(`${s.name} non più preferito`);
            } else {
                await api.post(`/suppliers/${s.id}/prefer`);
                toast.success(`★ ${s.name} aggiunto ai preferiti — sarà usato dal sistema di splitting ordini`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const openNewPrice = (supplier) => {
        setPriceForSupplier(supplier);
        setPriceDialogOpen(true);
    };

    const deletePrice = async (id) => {
        if (!window.confirm("Eliminare questo prezzo?")) return;
        await api.delete(`/supplier-prices/${id}`);
        await load();
    };

    const applyPrice = async (price) => {
        if (!window.confirm(`Applicare ${euroPrecise(price.price)}/${price.unit} di ${price.supplier_name} come nuovo costo per «${price.ingredient_name}»?`)) return;
        try {
            await api.post(`/supplier-prices/${price.id}/apply`);
            toast.success("Costo ingrediente aggiornato");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // ----- Bulk apply -----
    const toggleSelected = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllInSupplier = (sPrices) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const ids = sPrices.map((p) => p.id);
            const allOn = ids.every((id) => next.has(id));
            if (allOn) ids.forEach((id) => next.delete(id));
            else ids.forEach((id) => next.add(id));
            return next;
        });
    };

    const selectBestPrices = () => {
        const ids = Object.values(bestPriceByIngredient).map((p) => p.id);
        setSelected(new Set(ids));
        toast.message(`Selezionati ${ids.length} miglior prezz${ids.length === 1 ? "o" : "i"}`);
    };

    const clearSelection = () => setSelected(new Set());

    const applyBulk = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`Applicare ${ids.length} prezz${ids.length === 1 ? "o" : "i"} come nuovo costo dei rispettivi ingredienti?`)) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/supplier-prices/apply-bulk", { price_ids: ids });
            toast.success(`Aggiornati ${data.applied} su ${data.total} ingredienti${data.skipped?.length ? ` (${data.skipped.length} saltati)` : ""}`);
            clearSelection();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBulkBusy(false);
        }
    };

    const seedTestSupplier = async () => {
        if (
            !window.confirm(
                "Crea o aggiorna il fornitore TEST con tutti gli ingredienti? Verranno copiati prezzi e unità dal database interno."
            )
        )
            return;
        try {
            const { data } = await api.post("/suppliers/seed-test");
            toast.success(
                `Fornitore TEST pronto: ${data.added} prezzi nuovi, ${data.updated} aggiornati`
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <AppShell>
            <PageHeader
                eyebrow="Supply chain"
                title="Listino Fornitori"
                subtitle="I fornitori sono condivisi nella rete RENOVA360. Seleziona quelli che ti interessano dal Discover, oppure aggiungine di nuovi. Listini e fatture restano sempre privati."
                right={
                    <div className="hidden lg:flex items-center gap-2">
                        <button
                            onClick={() => setDiscoverOpen(true)}
                            data-testid="discover-suppliers-btn"
                            title="Sfoglia il pool globale dei fornitori"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-4 py-2 text-sm font-bold hover:bg-brand-goldDark transition shadow-gold"
                        >
                            <Network size={14} /> Scopri fornitori
                        </button>
                        <button
                            onClick={seedTestSupplier}
                            data-testid="seed-test-supplier-btn"
                            title="Crea/aggiorna fornitore TEST con tutti gli ingredienti"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-line text-brand-ink bg-paper px-4 py-2 text-sm font-semibold hover:border-brand-gold/60 hover:text-brand-navy transition"
                        >
                            <FlaskConical size={14} /> Fornitore TEST
                        </button>
                        {prices.length > 0 && (
                            <button
                                onClick={selectBestPrices}
                                data-testid="select-best-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 text-brand-navy bg-brand-gold/10 px-4 py-2 text-sm font-semibold hover:bg-brand-gold/20 transition"
                            >
                                <Sparkles size={14} /> Seleziona migliori
                            </button>
                        )}
                        <button
                            onClick={openNewSupplier}
                            data-testid="new-supplier-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-navyLight transition shadow-card"
                        >
                            <Plus size={16} /> Nuovo fornitore
                        </button>
                    </div>
                }
            />

            <PreferredBanner suppliers={suppliers} />

            {suppliers.length > 0 && (
                <SuppliersFilters
                    filters={filters}
                    setFilters={setFilters}
                    regionOptions={regionOptions}
                    cityOptions={cityOptions}
                    total={suppliers.length}
                    shown={filteredSuppliers.length}
                />
            )}

            {loading ? (
                <div className="py-12 text-center text-sm text-brand-muted">Caricamento…</div>
            ) : suppliers.length === 0 ? (
                <div className="bg-paper rounded-2xl border border-dashed border-brand-line p-12 text-center">
                    <Truck className="mx-auto text-brand-muted mb-3" size={40} strokeWidth={1.4} />
                    <div className="font-display text-2xl text-brand-ink">Nessun fornitore nel tuo portale</div>
                    <div className="mt-1 text-sm text-brand-muted max-w-md mx-auto">
                        Scopri fornitori dalla rete RENOVA360 oppure aggiungine uno nuovo. I fornitori che selezioni
                        compariranno qui per gli ordini e i listini privati.
                    </div>
                    <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
                        <button
                            onClick={() => setDiscoverOpen(true)}
                            data-testid="empty-discover-suppliers"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-5 py-2.5 text-sm font-bold hover:bg-brand-goldDark transition shadow-gold"
                        >
                            <Network size={14} /> Scopri fornitori
                        </button>
                        <button
                            onClick={openNewSupplier}
                            data-testid="empty-new-supplier"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-navyLight transition"
                        >
                            <Plus size={14} /> Aggiungi nuovo
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-5" data-testid="suppliers-list">
                    {filteredSuppliers.length === 0 ? (
                        <div className="bg-paper rounded-xl border border-dashed border-brand-line p-10 text-center" data-testid="suppliers-filters-empty">
                            <Search size={28} className="mx-auto text-brand-muted mb-2" strokeWidth={1.4} />
                            <div className="font-display text-lg text-brand-ink">Nessun fornitore corrisponde ai filtri</div>
                            <button
                                onClick={() => setFilters({ q: "", region: "", city: "", only_preferred: false })}
                                className="mt-3 inline-flex items-center gap-1 text-[12px] text-brand-navy hover:underline"
                                data-testid="suppliers-filters-clear"
                            >
                                <X size={12} /> Pulisci filtri
                            </button>
                        </div>
                    ) : null}
                    {filteredSuppliers.map((s) => {
                        const sPrices = pricesBySupplier[s.id] || [];
                        const isOpen = expanded.has(s.id);
                        return (
                            <div
                                key={s.id}
                                className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden"
                                data-testid={`supplier-card-${s.id}`}
                            >
                                <div className={`p-5 flex items-start gap-4 ${isOpen ? "border-b border-brand-line" : ""} bg-brand-cream/50`}>
                                    <button
                                        type="button"
                                        onClick={() => sPrices.length > 0 && toggleExpanded(s.id)}
                                        disabled={sPrices.length === 0}
                                        data-testid={`expand-toggle-${s.id}`}
                                        aria-label={isOpen ? "Nascondi alimenti" : "Vedi alimenti"}
                                        aria-expanded={isOpen}
                                        className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition ${
                                            sPrices.length === 0
                                                ? "bg-brand-navy/40 text-brand-gold/60 cursor-not-allowed"
                                                : "bg-brand-navy text-brand-gold hover:bg-brand-navyLight"
                                        }`}
                                    >
                                        <ChevronDown
                                            size={18}
                                            className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-display text-2xl text-brand-ink leading-tight flex items-center gap-2 flex-wrap">
                                            <span>{s.name}</span>
                                            {s.is_preferred && (
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5"
                                                    data-testid={`badge-preferred-${s.id}`}
                                                    title="Preferito: usato dal sistema di splitting ordini"
                                                >
                                                    <Star size={10} className="fill-amber-400" /> Preferito
                                                </span>
                                            )}
                                        </h3>
                                        <div className="mt-1 flex items-center gap-3 text-[12px] text-brand-muted flex-wrap">
                                            {s.category && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/15 text-brand-goldDark px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                                    {s.category}
                                                </span>
                                            )}
                                            {(s.city || s.region) && (
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin size={12} />
                                                    {[s.city, s.region].filter(Boolean).join(", ")}
                                                </span>
                                            )}
                                            {(s.contact || s.phone) && (
                                                <span className="inline-flex items-center gap-1"><Phone size={12} /> {s.phone || s.contact}</span>
                                            )}
                                            <span>{sPrices.length} prezz{sPrices.length === 1 ? "o" : "i"} in listino</span>
                                        </div>
                                        {s.notes && (
                                            <div className="mt-1 text-[12px] text-brand-muted italic font-display">{s.notes}</div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap justify-end">
                                        {sPrices.length > 0 && (
                                            <button
                                                onClick={() => toggleExpanded(s.id)}
                                                data-testid={`view-items-${s.id}`}
                                                aria-expanded={isOpen}
                                                className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition"
                                            >
                                                <List size={12} />
                                                {isOpen ? "Nascondi alimenti" : "Vedi alimenti"}
                                            </button>
                                        )}
                                        {sPrices.length > 0 && isOpen && (
                                            <button
                                                onClick={() => selectAllInSupplier(sPrices)}
                                                data-testid={`select-all-${s.id}`}
                                                title="Seleziona tutti i prezzi di questo fornitore"
                                                className="inline-flex items-center gap-1 rounded-full border border-brand-line text-brand-muted hover:border-brand-navy hover:text-brand-navy px-2.5 py-1.5 text-[11px] font-semibold transition"
                                            >
                                                <CheckCheck size={12} /> Tutti
                                            </button>
                                        )}
                                        <button
                                            onClick={() => openNewPrice(s)}
                                            data-testid={`add-price-${s.id}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-brand-gold text-brand-navy px-3 py-1.5 text-xs font-bold hover:bg-brand-goldDark transition"
                                        >
                                            <Plus size={14} /> Prezzo
                                        </button>
                                        <button
                                            onClick={() => togglePrefer(s)}
                                            data-testid={`prefer-sup-${s.id}`}
                                            title={s.is_preferred ? "Togli dai preferiti" : "Aggiungi ai preferiti (usati dal sistema di splitting ordini)"}
                                            className={`p-2 rounded-full transition ${
                                                s.is_preferred
                                                    ? "text-amber-500 bg-amber-50 hover:bg-amber-100"
                                                    : "text-brand-muted hover:text-amber-500 hover:bg-amber-50"
                                            }`}
                                        >
                                            <Star size={16} strokeWidth={2} className={s.is_preferred ? "fill-amber-400" : ""} />
                                        </button>
                                        <button onClick={() => openEditSupplier(s)} className="text-brand-muted hover:text-brand-ink p-2" data-testid={`edit-sup-${s.id}`}>
                                            <Edit3 size={14} />
                                        </button>
                                        <button onClick={() => deleteSupplier(s.id)} className="text-brand-muted hover:text-brand-danger p-2" data-testid={`del-sup-${s.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                {sPrices.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-brand-muted italic">
                                        Nessun prezzo per questo fornitore. Aggiungine uno per iniziare a confrontare.
                                    </div>
                                ) : isOpen ? (
                                    <div data-testid={`supplier-items-${s.id}`}>
                                        {sPrices.map((p, i) => {
                                            const best = bestPriceByIngredient[p.ingredient_id];
                                            const isBest = best && best.id === p.id;
                                            const isChecked = selected.has(p.id);
                                            // Fallback: se ingredient_name manca (vecchi record OCR) lo recupero localmente
                                            const ingLocal = ingredients.find((x) => x.id === p.ingredient_id);
                                            const ingName = p.ingredient_name || ingLocal?.name || "Ingrediente non trovato";
                                            const ingCat = p.ingredient_category || ingLocal?.category;
                                            // Applicato? Confronto cost_per_unit dell'ingrediente con il prezzo del fornitore (convertito in unità base)
                                            let isApplied = false;
                                            if (ingLocal && Number.isFinite(ingLocal.cost_per_unit)) {
                                                const factor = convertToBase(1, p.unit, ingLocal.unit);
                                                if (factor && factor > 0) {
                                                    const effective = Number(p.price) / factor;
                                                    isApplied = Math.abs(effective - Number(ingLocal.cost_per_unit)) < 0.001;
                                                }
                                            }
                                            return (
                                                <div
                                                    key={p.id}
                                                    className={`px-5 py-3.5 flex items-center gap-3 ${i > 0 ? "border-t border-brand-line" : ""} ${isChecked ? "bg-brand-gold/8" : ""}`}
                                                    data-testid={`price-row-${p.id}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleSelected(p.id)}
                                                        data-testid={`price-check-${p.id}`}
                                                        aria-label={`Seleziona prezzo di ${ingName}`}
                                                        className="h-4 w-4 accent-brand-navy shrink-0 cursor-pointer"
                                                    />
                                                    <span
                                                        className="w-1.5 h-10 rounded-full shrink-0"
                                                        style={{ background: catColor(ingCat) }}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <div className={`text-sm font-semibold truncate ${ingLocal || p.ingredient_name ? "text-brand-ink" : "text-brand-danger"}`}>
                                                                {ingName}
                                                            </div>
                                                            {isBest && (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-success/12 text-brand-success px-2 py-0.5 text-[9px] uppercase tracking-wider font-bold">
                                                                    <Award size={10} /> Miglior prezzo
                                                                </span>
                                                            )}
                                                        </div>
                                                        {p.notes && (
                                                            <div className="text-[11px] text-brand-muted italic mt-0.5">{p.notes}</div>
                                                        )}
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="font-display text-xl text-brand-ink leading-none">{euroPrecise(p.price)}</div>
                                                        <div className="text-[10px] uppercase tracking-wider text-brand-muted mt-0.5">per {p.unit}</div>
                                                    </div>
                                                    {isApplied ? (
                                                        <span
                                                            data-testid={`applied-${p.id}`}
                                                            title="Questo prezzo è il costo attuale dell'ingrediente"
                                                            className="inline-flex items-center gap-1 rounded-full bg-brand-success/15 text-brand-success border border-brand-success/30 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider"
                                                        >
                                                            <Check size={12} strokeWidth={3} /> Applicato
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => applyPrice({ ...p, ingredient_name: ingName })}
                                                            data-testid={`apply-${p.id}`}
                                                            title="Applica questo prezzo come nuovo costo dell'ingrediente"
                                                            className="inline-flex items-center gap-1 rounded-full border border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition"
                                                        >
                                                            <TrendingDown size={12} /> Applica
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deletePrice(p.id)}
                                                        data-testid={`del-price-${p.id}`}
                                                        className="text-brand-muted hover:text-brand-danger p-1.5"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => toggleExpanded(s.id)}
                                        data-testid={`expand-summary-${s.id}`}
                                        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-[12px] text-brand-muted hover:text-brand-ink hover:bg-brand-cream transition"
                                    >
                                        <span>
                                            <span className="font-semibold text-brand-ink">{sPrices.length}</span>{" "}
                                            alimenti in listino
                                            {(() => {
                                                const bestCount = sPrices.filter(
                                                    (p) => bestPriceByIngredient[p.ingredient_id]?.id === p.id,
                                                ).length;
                                                return bestCount > 0 ? (
                                                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-success/12 text-brand-success px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                                                        <Award size={10} /> {bestCount} miglior{bestCount === 1 ? "e" : "i"}
                                                    </span>
                                                ) : null;
                                            })()}
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-brand-navy">
                                            Espandi <ChevronDown size={12} />
                                        </span>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <button
                onClick={openNewSupplier}
                data-testid="suppliers-fab"
                className="lg:hidden fixed right-5 bottom-24 z-40 h-14 w-14 rounded-full bg-brand-gold text-brand-navy shadow-gold flex items-center justify-center"
            >
                <Plus size={24} strokeWidth={2.6} />
            </button>

            {selected.size > 0 && (
                <div
                    data-testid="bulk-apply-bar"
                    className="fixed left-1/2 -translate-x-1/2 bottom-6 lg:bottom-8 z-[60] bg-brand-navy text-white rounded-full shadow-card border border-brand-gold/40 pl-5 pr-2 py-2 flex items-center gap-3 max-w-[95vw]"
                >
                    <span className="text-sm font-semibold whitespace-nowrap">
                        {selected.size} selezionat{selected.size === 1 ? "o" : "i"}
                    </span>
                    <button
                        onClick={clearSelection}
                        data-testid="bulk-clear-btn"
                        className="text-white/70 hover:text-white p-1"
                        aria-label="Annulla selezione"
                    >
                        <X size={16} />
                    </button>
                    <button
                        onClick={applyBulk}
                        disabled={bulkBusy}
                        data-testid="bulk-apply-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-5 py-2 text-sm font-bold hover:bg-brand-goldDark transition disabled:opacity-60"
                    >
                        <TrendingDown size={14} />
                        {bulkBusy ? "Applicazione…" : `Applica ${selected.size}`}
                    </button>
                </div>
            )}

            <SupplierDialog
                open={supDialogOpen}
                onOpenChange={setSupDialogOpen}
                initial={editingSupplier}
                onSave={saveSupplier}
            />
            <SupplierDiscoverDialog
                open={discoverOpen}
                onOpenChange={setDiscoverOpen}
                onChanged={load}
            />
            <PriceDialog
                open={priceDialogOpen}
                onOpenChange={setPriceDialogOpen}
                supplier={priceForSupplier}
                ingredients={ingredients}
                onSaved={load}
            />
        </AppShell>
    );
}

function SupplierDialog({ open, onOpenChange, initial, onSave }) {
    const [form, setForm] = useState({
        name: "",
        contact: "",
        email: "",
        phone: "",
        region: "",
        city: "",
        vat: "",
        website: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm({
                name: "",
                contact: "",
                email: "",
                phone: "",
                region: "",
                city: "",
                vat: "",
                website: "",
                notes: "",
                ...(initial || {}),
            });
            setError("");
        }
    }, [open, initial]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        setError("");
        if (!form.name.trim()) { setError("Nome obbligatorio"); return; }
        setSaving(true);
        try {
            await onSave(form);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg bg-white p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="supplier-dialog">
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-brand-line shrink-0">
                    <DialogTitle className="font-display text-2xl text-brand-ink">
                        {form.id ? "Modifica fornitore" : "Nuovo fornitore"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        {form.id
                            ? "Le modifiche valgono per tutti gli utenti che usano questo fornitore nella rete."
                            : "Sarà visibile anche agli altri clienti RENOVA360. Listini e fatture restano comunque privati."}
                    </DialogDescription>
                </DialogHeader>
                <div className="p-6 space-y-4 overflow-y-auto">
                    {error && (
                        <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5">
                            {error}
                        </div>
                    )}
                    <LabeledInput label="Nome*" value={form.name} onChange={set("name")} testid="sup-name" />
                    <div className="grid grid-cols-2 gap-3">
                        <LabeledInput label="Regione" value={form.region} onChange={set("region")} testid="sup-region" />
                        <LabeledInput label="Città" value={form.city} onChange={set("city")} testid="sup-city" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <LabeledInput label="Telefono" value={form.phone} onChange={set("phone")} testid="sup-phone" />
                        <LabeledInput label="Email" value={form.email} onChange={set("email")} testid="sup-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <LabeledInput label="P.IVA" value={form.vat} onChange={set("vat")} testid="sup-vat" />
                        <LabeledInput label="Sito web" value={form.website} onChange={set("website")} testid="sup-website" />
                    </div>
                    <LabeledInput label="Contatto (referente)" value={form.contact} onChange={set("contact")} testid="sup-contact" />
                    <div>
                        <div className="eyebrow text-brand-ink/60 mb-1.5">Note pubbliche</div>
                        <textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => set("notes")(e.target.value)}
                            data-testid="sup-notes"
                            placeholder="Es. consegne lun-gio, specializzato in pesce fresco"
                            className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold resize-none"
                        />
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-brand-line bg-brand-cream shrink-0">
                    <button
                        onClick={submit}
                        disabled={saving}
                        data-testid="sup-save"
                        className="w-full rounded-full bg-brand-navy text-white py-3 text-sm font-semibold hover:bg-brand-navyLight transition disabled:opacity-60"
                    >
                        <span className="inline-flex items-center gap-2"><Save size={14} /> {saving ? "Salvataggio…" : "Salva"}</span>
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PriceDialog({ open, onOpenChange, supplier, ingredients, onSaved }) {
    const [ing, setIng] = useState(null);
    const [price, setPrice] = useState(0);
    const [unit, setUnit] = useState("kg");
    const [notes, setNotes] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setIng(null); setPrice(0); setUnit("kg"); setNotes(""); setError("");
        }
    }, [open]);

    useEffect(() => {
        if (ing) setUnit(ing.unit);
    }, [ing]);

    const save = async () => {
        setError("");
        if (!supplier || !ing) { setError("Seleziona un ingrediente"); return; }
        if (!(Number(price) >= 0)) { setError("Prezzo non valido"); return; }
        setSaving(true);
        try {
            await api.post("/supplier-prices", {
                supplier_id: supplier.id,
                ingredient_id: ing.id,
                price: Number(price),
                unit,
                notes,
            });
            onOpenChange(false);
            await onSaved();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-md bg-white p-0 overflow-hidden" data-testid="price-dialog">
                    <DialogHeader className="px-6 pt-6 pb-3 border-b border-brand-line">
                        <DialogTitle className="font-display text-2xl text-brand-ink">
                            Aggiungi prezzo
                        </DialogTitle>
                        <DialogDescription className="text-sm text-brand-muted">
                            {supplier ? `Per ${supplier.name}` : ""}. Se esiste già una voce per lo stesso ingrediente viene aggiornata.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-6 space-y-4">
                        {error && (
                            <div className="rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5">
                                {error}
                            </div>
                        )}
                        <div>
                            <div className="eyebrow text-brand-ink/60 mb-1.5">Ingrediente</div>
                            <button
                                onClick={() => setPickerOpen(true)}
                                data-testid="price-pick-ing"
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-brand-cream border border-brand-line text-left hover:border-brand-gold transition"
                            >
                                {ing ? (
                                    <div>
                                        <div className="text-sm font-semibold text-brand-ink">{ing.name}</div>
                                        <div className="text-[11px] text-brand-muted">costo attuale {euroPrecise(ing.cost_per_unit)}/{ing.unit}</div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-brand-muted">Scegli un ingrediente…</div>
                                )}
                                <ChevronRight size={16} className="text-brand-muted" />
                            </button>
                        </div>
                        <div className="grid grid-cols-[1fr_auto] gap-3">
                            <div>
                                <div className="eyebrow text-brand-ink/60 mb-1.5">Prezzo (€)</div>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    data-testid="price-value"
                                    className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                                />
                            </div>
                            <div>
                                <div className="eyebrow text-brand-ink/60 mb-1.5">Unità</div>
                                <UnitPills value={unit} onChange={setUnit} testid="price-unit" />
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow text-brand-ink/60 mb-1.5">Note</div>
                            <input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="price-notes"
                                placeholder="Es. pesce selvaggio, consegna lun-gio"
                                className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
                            />
                        </div>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="price-save"
                            className="w-full rounded-full bg-brand-navy text-white py-3 text-sm font-semibold hover:bg-brand-navyLight transition disabled:opacity-60"
                        >
                            {saving ? "Salvataggio…" : "Salva prezzo"}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
            <IngredientPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                ingredients={ingredients}
                onPick={setIng}
            />
        </>
    );
}

function LabeledInput({ label, value, onChange, testid }) {
    return (
        <div>
            <div className="eyebrow text-brand-ink/60 mb-1.5">{label}</div>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-4 py-3 rounded-lg bg-white border border-brand-line text-[15px] focus:outline-none focus:border-brand-gold"
            />
        </div>
    );
}

function SuppliersFilters({ filters, setFilters, regionOptions, cityOptions, total, shown }) {
    const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
    const isFiltered = filters.q || filters.region || filters.city || filters.only_preferred;
    return (
        <div
            className="mb-5 rounded-2xl bg-paper border border-brand-line shadow-card px-5 py-4"
            data-testid="suppliers-filters"
        >
            <div className="flex items-center gap-2 mb-3">
                <Filter size={14} className="text-brand-gold" />
                <span className="eyebrow text-brand-ink/70">Filtri rapidi</span>
                <span className="text-[11px] text-brand-muted ml-auto">
                    {isFiltered ? `${shown} / ${total} fornitori` : `${total} fornitori`}
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative lg:col-span-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input
                        type="text"
                        value={filters.q}
                        onChange={(e) => update("q", e.target.value)}
                        placeholder="Cerca per nome, categoria, città…"
                        data-testid="suppliers-filter-search"
                        className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                    />
                </div>
                <select
                    value={filters.region}
                    onChange={(e) => update("region", e.target.value)}
                    data-testid="suppliers-filter-region"
                    className="px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                >
                    <option value="">Tutte le regioni</option>
                    {regionOptions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                    ))}
                </select>
                <select
                    value={filters.city}
                    onChange={(e) => update("city", e.target.value)}
                    data-testid="suppliers-filter-city"
                    className="px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                >
                    <option value="">Tutte le città</option>
                    {cityOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 text-[12px] text-brand-ink cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filters.only_preferred}
                        onChange={(e) => update("only_preferred", e.target.checked)}
                        data-testid="suppliers-filter-preferred"
                        className="h-3.5 w-3.5 accent-amber-500"
                    />
                    <Star size={12} className="text-amber-500 fill-amber-400" /> Mostra solo i preferiti
                </label>
                {isFiltered && (
                    <button
                        onClick={() => setFilters({ q: "", region: "", city: "", only_preferred: false })}
                        data-testid="suppliers-filter-reset"
                        className="inline-flex items-center gap-1 text-[12px] text-brand-muted hover:text-brand-ink"
                    >
                        <X size={12} /> Reset
                    </button>
                )}
            </div>
        </div>
    );
}



function PreferredBanner({ suppliers }) {
    const preferred = suppliers.filter((s) => s.is_preferred);
    if (suppliers.length === 0) return null;
    if (preferred.length === 0) {
        return (
            <div
                className="mb-5 rounded-2xl bg-amber-50/60 border border-amber-200 px-5 py-3 flex items-start gap-3"
                data-testid="preferred-banner-empty"
            >
                <Star size={16} className="text-amber-500 mt-0.5 shrink-0" />
                <div className="text-[13px] text-amber-900 leading-relaxed">
                    <strong>Imposta i tuoi fornitori preferiti</strong> con il pulsante ★ accanto al nome.
                    Solo quelli verranno usati dal sistema intelligente che divide automaticamente l'ordine
                    sui fornitori col miglior prezzo. Senza preferiti, lo splitting considera <strong>tutti</strong> i fornitori del tuo portale.
                </div>
            </div>
        );
    }
    return (
        <div
            className="mb-5 rounded-2xl bg-amber-50 border border-amber-300 px-5 py-3 flex items-start gap-3"
            data-testid="preferred-banner"
        >
            <Star size={16} className="text-amber-500 fill-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
                <div className="text-[13px] text-amber-900">
                    <strong>{preferred.length} fornitor{preferred.length === 1 ? "e" : "i"} preferit{preferred.length === 1 ? "o" : "i"}</strong> attiv{preferred.length === 1 ? "o" : "i"} — il sistema dividerà gli ordini solo tra loro per il miglior prezzo.
                </div>
                <div className="mt-1 text-[11px] text-amber-800 truncate">
                    {preferred.slice(0, 5).map((s) => s.name).join(" · ")}
                    {preferred.length > 5 && ` · +${preferred.length - 5} altri`}
                </div>
            </div>
        </div>
    );
}
